// @flow

import * as React from 'react';
import {useIntl} from 'react-intl';
import {BodyExtra, BodyMono} from 'sail/Text';
import LineStack from './LineStack';
import KeyValueRow from './KeyValueRow';
import {getSurroundingChars, isObject, COLORS} from './helpers';
import {messages} from './messages';

type Props = {
  data: Object,
  rootKey?: string,
  isRoot?: boolean,
  minCollapseSize?: number,
  renderValue: ({key: string, value: any}) => React.Node,
};

const ObjectDisplay = ({
  data,
  rootKey,
  isRoot,
  minCollapseSize = 0,
  renderValue,
}: Props) => {
  const {formatMessage} = useIntl();
  const childCount = Object.keys(data).length;
  const isEmpty = childCount === 0;

  const [isCollapsed, setCollapsed] = React.useState(
    () => !isRoot && childCount > minCollapseSize,
  );

  if ((isObject(data) || Array.isArray(data)) && (isCollapsed || isEmpty)) {
    const [leading, trailing] = getSurroundingChars(Array.isArray(data));

    return (
      <KeyValueRow
        onClick={isEmpty ? null : () => setCollapsed(false)}
        canCopy={isRoot}
        data={data}
      >
        {!!rootKey && (
          <>
            <pre style={{color: COLORS.string}}>{`"${rootKey}"`}</pre>
            <pre>{': '}</pre>
          </>
        )}
        <pre style={{color: COLORS.default}}>
          {isEmpty ? (
            `${leading}${trailing}`
          ) : (
            <>
              {leading}
              <span style={{margin: '0 4px'}}>
                <BodyExtra
                  color="gray300"
                  style={{lineHeight: 0}}
                >{`… `}</BodyExtra>
                <BodyMono color="gray300" style={{lineHeight: 0}}>
                  {formatMessage(messages.numberOfItems, {count: childCount})}
                </BodyMono>
              </span>
              {trailing}
            </>
          )}
        </pre>
        {!isRoot && <pre>{','}</pre>}
      </KeyValueRow>
    );
  }

  const lines = Object.entries(data).map(([key, value], idx) =>
    isObject(value) || Array.isArray(value) ? (
      <ObjectDisplay
        data={value}
        rootKey={key}
        minCollapseSize={minCollapseSize}
        renderValue={renderValue}
        key={idx}
      />
    ) : (
      <KeyValueRow hasTrailingComma={!isRoot} key={idx}>
        {isObject(data) && (
          <>
            <pre style={{color: COLORS.string}}>{`"${key}"`}</pre>
            <pre>{': '}</pre>
          </>
        )}
        <pre
          style={{
            color: COLORS[typeof value] ?? COLORS.default,
            overflow: 'visible',
          }}
        >
          {renderValue({key, value})}
        </pre>
        <pre>{','}</pre>
      </KeyValueRow>
    ),
  );

  return (
    <LineStack
      rootKey={rootKey}
      hasTrailingComma={!isRoot}
      onClick={() => setCollapsed(true)}
      isArray={Array.isArray(data)}
      canCopy={isRoot}
      data={data}
    >
      {lines}
    </LineStack>
  );
};

export default ObjectDisplay;
